// @flow

import React, { useState } from 'react';
import * as Yup from 'yup';
import { i18n, Analytics, getCurrencySymbol } from 'shared/utils';
import { graphql, QueryRenderer } from 'react-relay';
import relayEnvironment from 'shared/gql/relayEnvironment';
import * as Actions from 'main-app/store/Actions';
import AddPurchaseOrderMutation from 'main-app/mutations/AddPurchaseOrder';
import Button from 'shared/components/common/Button';
import Loader from 'shared/components/common/Loader';
import SelectItemField from 'shared/components/form/SelectItemField';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'shared/components/modal';
import {
  Formik,
  Form,
  FieldGroup,
  FieldGroupRow,
} from 'shared/components/form';
import styled from 'styled-components';

type Props = {
  onClose: () => void,
  onSuccess?: Object => void,
  history: *,
};

const LineItemsSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
`;

const LineItemsSectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #1a1a1a;
`;

const LineItemRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr auto;
  gap: 12px;
  margin-bottom: 12px;
  align-items: flex-end;
`;

const RemoveButton = styled.button`
  background: #ff4444;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  height: 38px;

  &:hover {
    background: #cc0000;
  }
`;

const AddRowButton = styled.button`
  background: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  margin-top: 8px;

  &:hover {
    background: #357abd;
  }
`;

const CurrencyInputWrapper = styled.div`
  position: relative;

  & > span {
    position: absolute;
    left: 10px;
    top: ${p => (p.hasLabel ? '30px' : '8px')};
    color: #666;
    font-size: 14px;
    pointer-events: none;
    z-index: 1;
  }

  input {
    padding-left: 22px;
  }
`;

const TotalSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  font-size: 18px;
  font-weight: 600;
`;

const CreatePurchaseOrderModal = ({ onClose, onSuccess, history }: Props) => {
  const [lineItems, setLineItems] = useState([
    {
      item: null,
      quantity: '',
      unitPrice: '',
      totalPrice: 0,
    },
  ]);

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      {
        item: null,
        quantity: '',
        unitPrice: '',
        totalPrice: 0,
      },
    ]);
  };

  const removeLineItem = index => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const updateLineItem = (index, field, value, extraFields = {}) => {
    const updated = [...lineItems];
    updated[index][field] = value;

    // Apply any extra fields (e.g. auto-populated unitPrice)
    Object.keys(extraFields).forEach(key => {
      updated[index][key] = extraFields[key];
    });

    // Auto-calculate total price
    if (field === 'quantity' || field === 'unitPrice' || extraFields.unitPrice !== undefined) {
      const qty = parseFloat(updated[index].quantity) || 0;
      const price = parseFloat(updated[index].unitPrice) || 0;
      updated[index].totalPrice = qty * price;
    }

    setLineItems(updated);
  };

  const calculateTotalAmount = () => {
    return lineItems.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
  };

  const formatCurrency = (amount: number) => {
    return `${getCurrencySymbol()}${amount.toFixed(2)}`;
  };

  return (
    <QueryRenderer
      environment={relayEnvironment}
      dataFrom="STORE_THEN_NETWORK"
      query={graphql`
        query CreatePurchaseOrderModalQuery {
          vendors(first: null) {
            edges {
              node {
                id
                vendorNumber
                name
              }
            }
          }
        }
      `}
      variables={null}
      render={({ props }) => {
        if (!props) {
          return (
            <Modal maxWidth={1000}>
              <ModalBody withPadding>
                <Loader />
              </ModalBody>
            </Modal>
          );
        }

        const vendorOptions = props.vendors.edges.map(({ node }) => ({
          value: node.id,
          label: `${node.vendorNumber || ''} - ${node.name}`,
          data: node,
        }));

        return (
          <Modal maxWidth={1000}>
            <Formik
              initialValues={{
                vendor: null,
                expectedDeliveryDate: '',
                notes: '',
              }}
              validationSchema={Yup.object().shape({
                vendor: Yup.object()
                  .nullable()
                  .required('Please select a vendor'),
                expectedDeliveryDate: Yup.string().nullable(),
                notes: Yup.string().nullable(),
              })}
              onSubmit={async (values: *, { setSubmitting }: *) => {
                setSubmitting(true);

                // Validate line items
                const validLineItems = lineItems.filter(
                  item => item.item && item.quantity && item.unitPrice
                );

                if (validLineItems.length === 0) {
                  Actions.alertNotification(
                    i18n.t('Please add at least one line item'),
                    'error'
                  );
                  setSubmitting(false);
                  return;
                }

                const input = {
                  vendorId: values.vendor.value,
                  expectedDeliveryDate: values.expectedDeliveryDate || null,
                  notes: values.notes || null,
                  lineItems: validLineItems.map(item => ({
                    itemId: item.item.value,
                    quantity: parseFloat(item.quantity),
                    unitPrice: parseFloat(item.unitPrice),
                  })),
                };

                try {
                  const response = await AddPurchaseOrderMutation.commit({
                    variables: {
                      input,
                    },
                  });

                  const newPO = response.addPurchaseOrder.purchaseOrderEdge.node;

                  onClose();

                  if (onSuccess) {
                    onSuccess(newPO);
                  }

                  Analytics.trackEvent('Create Purchase Order', {
                    vendorId: values.vendor.value,
                    lineItemCount: validLineItems.length,
                    totalAmount: calculateTotalAmount(),
                  });

                  Actions.alertNotification(
                    i18n.t('Purchase Order Successfully Created'),
                    'success'
                  );

                  // Navigate to PO detail page
                  if (history) {
                    history.push(`/purchase-order/${newPO.id}`);
                  }
                } catch (e) {
                  setSubmitting(false);
                  Actions.alertNotification(e.message, 'error');
                }
              }}
              render={({
                values,
                errors,
                isValid,
                isSubmitting,
                handleSubmit,
                setFieldValue,
              }) => (
                <Form>
                  <ModalHeader
                    header={i18n.t('Create Purchase Order')}
                    onClose={onClose}
                  />
                  <ModalBody withPadding>
                    <FieldGroup
                      type="select"
                      name="vendor"
                      label={i18n.t('Vendor')}
                      placeholder={i18n.t('Select Vendor')}
                      options={vendorOptions}
                      error={errors.vendor}
                      defaultOptions
                    />

                    <FieldGroupRow
                      left={
                        <FieldGroup
                          type="date"
                          name="expectedDeliveryDate"
                          label={i18n.t('Expected Delivery Date')}
                          error={errors.expectedDeliveryDate}
                        />
                      }
                      right={<div />}
                    />

                    <FieldGroup
                      type="textarea"
                      name="notes"
                      label={i18n.t('Notes')}
                      placeholder={i18n.t('Additional notes...')}
                      error={errors.notes}
                      rows={2}
                    />

                    <LineItemsSection>
                      <LineItemsSectionTitle>
                        {i18n.t('Line Items')}
                      </LineItemsSectionTitle>

                      {lineItems.map((item, index) => (
                        <LineItemRow key={index}>
                          <SelectItemField
                            rawField
                            name={`lineitem-item-${index}`}
                            label={index === 0 ? i18n.t('Item') : null}
                            value={item.item}
                            onChange={selectedItem => {
                              const extra = {};
                              if (selectedItem?.data?.cost != null) {
                                extra.unitPrice = String(selectedItem.data.cost);
                              }
                              updateLineItem(index, 'item', selectedItem, extra);
                            }}
                          />
                          <FieldGroup
                            rawField
                            name={`lineitem-quantity-${index}`}
                            label={index === 0 ? i18n.t('Quantity') : null}
                            placeholder="0"
                            type="number"
                            step="0.01"
                            value={item.quantity}
                            onChange={value =>
                              updateLineItem(index, 'quantity', value)
                            }
                          />
                          <CurrencyInputWrapper hasLabel={index === 0}>
                            <span>{getCurrencySymbol()}</span>
                            <FieldGroup
                              rawField
                              name={`lineitem-unitprice-${index}`}
                              label={index === 0 ? i18n.t('Unit Price') : null}
                              placeholder="0.00"
                              type="number"
                              step="0.01"
                              value={item.unitPrice}
                              onChange={value =>
                                updateLineItem(index, 'unitPrice', value)
                              }
                            />
                          </CurrencyInputWrapper>
                          <FieldGroup
                            rawField
                            name={`lineitem-total-${index}`}
                            label={index === 0 ? i18n.t('Total') : null}
                            value={formatCurrency(item.totalPrice)}
                            disabled
                          />
                          <RemoveButton
                            type="button"
                            onClick={() => removeLineItem(index)}
                            disabled={lineItems.length === 1}
                          >
                            {i18n.t('Remove')}
                          </RemoveButton>
                        </LineItemRow>
                      ))}

                      <AddRowButton type="button" onClick={addLineItem}>
                        {i18n.t('+ Add Line Item')}
                      </AddRowButton>

                      <TotalSection>
                        <span>{i18n.t('Total Amount:')}</span>
                        <span>{formatCurrency(calculateTotalAmount())}</span>
                      </TotalSection>
                    </LineItemsSection>
                  </ModalBody>
                  <ModalFooter>
                    <Button
                      type="submit"
                      theme="blue"
                      disabled={!isValid || lineItems.length === 0}
                      loading={isSubmitting}
                      onClick={handleSubmit}
                    >
                      {i18n.t('Create Purchase Order')}
                    </Button>
                  </ModalFooter>
                </Form>
              )}
            />
          </Modal>
        );
      }}
    />
  );
};

CreatePurchaseOrderModal.defaultProps = {
  onSuccess: undefined,
};

export default CreatePurchaseOrderModal;
